import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { isEmpty, isNil } from 'lodash'
import { IconSearch, IconTrash, IconPlus, IconPencil, IconCheck, IconX } from '@tabler/icons-react'

import { BasicModal } from '@/components/layout/modals/BasicModal'
import { Banner } from '@/components/banners/Banner'
import { Button } from '@/components/buttons/Button'
import { Pill } from '@/components/pills/Pill'
import { Loader } from '@/components/loaders/Loader'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { TextInput } from '@/components/forms/TextInput'
import { toast } from '@/components/toasts/toast.helpers'
import { fetchTaxonomyOptions, saveTaxonomyOptions } from '@/reducers/playbooks/playbook.actions'

import { isOptionGated } from '../helpers/fieldLinks.helpers'

import { buildTaxonomyEdit } from '../helpers'

const MAX_OPTIONS = 150
const MAX_OPTION_LENGTH = 60
const TOO_LONG_MESSAGE = `Options must be ${MAX_OPTION_LENGTH} characters or less`
const AT_LIMIT_MESSAGE = `A pick list can have at most ${MAX_OPTIONS} options`

// Editor for a pick_list field's options. Loads the field's taxonomy
// by uuid; the search box doubles as a creator — when the query matches no
// existing option exactly, a "Create" row appears that adds it to the list.
// Adds and deletes accumulate locally, then "Save changes" overwrites the
// taxonomy (reusing option uuids, sending null for newly added ones).
//
// `onLinkToDefault` is offered when the options are this configuration's own
// copy of an org default's — the state a fork lands in once saved. Without it a
// fork is a one-way door: nothing on the row or in here says the default still
// exists, let alone how to go back to it.
//
// `appliesToInheritingPlaybooks` is the same relationship seen from the other
// end — the org defaults editor, where this taxonomy is the one being inherited
// — so editing is allowed but says how far the change reaches.
//
// `inheritedFromDefault` marks a taxonomy the org defaults own and every
// inheriting playbook shares: the list becomes a record — searchable, not
// editable — because a save here would change the options for all of them. The
// options are edited on the defaults page, or `onBreakFromDefault` forks a copy
// belonging to this configuration alone (it receives the options on screen, for
// the caller to stage until the flow save mints a taxonomy from them).
export const PickListOptionsModal = ({
  show,
  onClose,
  fieldName,
  taxonomyUuid,
  organizationId,
  onApplyForTesting,
  pendingEdit,
  onSaved,
  gatedOperands = [],
  inheritedFromDefault = false,
  onBreakFromDefault,
  onLinkToDefault,
  appliesToInheritingPlaybooks = false,
}) => {
  const dispatch = useDispatch()
  // Stable per-row keys (option names/uuids aren't reliable keys for new rows).
  const clientIdRef = useRef(0)
  const withClientIds = (options) =>
    options.map((option) => {
      clientIdRef.current += 1
      return { ...option, clientId: clientIdRef.current }
    })

  const [root, setRoot] = useState(null)
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [errored, setErrored] = useState(false)
  const [saving, setSaving] = useState(false)
  const [search, setSearch] = useState('')
  // The option being renamed inline, plus its draft name.
  const [editingClientId, setEditingClientId] = useState(null)
  const [editingName, setEditingName] = useState('')
  // The search box is also the creator, so the caret goes back to it after each
  // create — several options can be added without reaching for the mouse.
  const searchRef = useRef(null)
  // A rename takes the caret as its row opens, for the same reason.
  const editNameRef = useRef(null)

  useEffect(() => {
    if (!show || !taxonomyUuid) {
      return undefined
    }

    let active = true
    setLoading(true)
    setErrored(false)
    setSearch('')
    setEditingClientId(null)
    // Edits already applied for testing are the list the user last left, so
    // they reopen in place of the saved taxonomy rather than being discarded.
    if (pendingEdit) {
      setRoot(pendingEdit.root)
      setOptions(withClientIds(pendingEdit.options))
      setLoading(false)
      return undefined
    }
    dispatch(fetchTaxonomyOptions(taxonomyUuid))
      .then((result) => {
        if (active) {
          setRoot(result.root)
          setOptions(withClientIds(result.options))
        }
      })
      .catch((err) => {
        console.error(err)
        if (active) {
          setErrored(true)
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false)
        }
      })

    return () => {
      active = false
    }
  }, [show, taxonomyUuid])

  useEffect(() => {
    if (!isNil(editingClientId)) {
      editNameRef.current?.focus()
    }
  }, [editingClientId])

  const handleRemoveOption = (clientId) =>
    setOptions((current) => current.filter((option) => option.clientId !== clientId))

  const handleStartEdit = (option) => {
    setEditingClientId(option.clientId)
    setEditingName(option.name)
  }

  const handleCancelEdit = () => {
    setEditingClientId(null)
    setEditingName('')
  }

  const editNameTooLong = editingName.trim().length > MAX_OPTION_LENGTH

  // Rename keeps the option's uuid (reused on save) and its description.
  const handleConfirmEdit = () => {
    const trimmed = editingName.trim()
    if (isEmpty(trimmed) || editNameTooLong) {
      return
    }
    setOptions((current) =>
      current.map((option) =>
        option.clientId === editingClientId ? { ...option, name: trimmed } : option
      )
    )
    handleCancelEdit()
  }

  const query = search.trim()
  const queryLower = query.toLowerCase()
  const visibleOptions = isEmpty(queryLower)
    ? options
    : options.filter(
        (option) =>
          option.name.toLowerCase().includes(queryLower) ||
          (option.description ?? '').toLowerCase().includes(queryLower)
      )

  // Offer creation only when the query names something not already in the list.
  const exactMatchExists = options.some((option) => option.name.trim().toLowerCase() === queryLower)
  const showCreate = !inheritedFromDefault && !isEmpty(query) && !exactMatchExists
  const queryTooLong = query.length > MAX_OPTION_LENGTH
  const atLimit = options.length >= MAX_OPTIONS

  const handleCreateOption = () => {
    if (queryTooLong || atLimit) {
      return
    }
    clientIdRef.current += 1
    // Prepend so the new option lands at the top, then clear the search so it's
    // visible and the user can immediately add another.
    setOptions((current) => [
      { clientId: clientIdRef.current, taxonomy_uuid: null, name: query, description: '' },
      ...current,
    ])
    setSearch('')
    searchRef.current?.focus()
  }

  // Block the inline-test apply mid-edit, mid-save, or when above the cap.
  const optionsInvalid =
    loading || errored || saving || !isNil(editingClientId) || options.length > MAX_OPTIONS

  // Lift the current (unsaved) edits so the test can derive inline options and a
  // later save persists them with option uuids preserved.
  const handleApplyForTesting = () => {
    onApplyForTesting(buildTaxonomyEdit(root, options))
    onClose()
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const result = await dispatch(
        saveTaxonomyOptions({
          root,
          organizationId,
          options: options.map((option) => ({
            taxonomy_uuid: option.taxonomy_uuid ?? null,
            name: option.name.trim(),
            description: (option.description ?? '').trim(),
          })),
        })
      )
      setRoot(result.root)
      setOptions(withClientIds(result.options))
      onSaved?.(result)
      toast.success('Options saved')
    } catch (err) {
      console.error(err)
      toast.error('Failed to save options', { cause: err })
    } finally {
      setSaving(false)
    }
  }

  const renderOptionRow = (option) => {
    if (option.clientId === editingClientId) {
      return (
        <li key={option.clientId} className="pick-list-options__row">
          <TextInput
            ref={editNameRef}
            small
            value={editingName}
            onChange={(event) => setEditingName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                handleConfirmEdit()
              } else if (event.key === 'Escape') {
                event.preventDefault()
                handleCancelEdit()
              }
            }}
            placeholder="Option name"
            data-testid="pick-list-edit-input"
          />
          <Button
            basic
            small
            square
            type="button"
            tooltip="Save name"
            disabled={isEmpty(editingName.trim()) || editNameTooLong}
            disabledTooltip={editNameTooLong ? TOO_LONG_MESSAGE : undefined}
            onClick={handleConfirmEdit}
            data-testid="pick-list-edit-confirm"
          >
            <IconCheck />
          </Button>
          <Button
            basic
            small
            square
            type="button"
            tooltip="Cancel"
            onClick={handleCancelEdit}
            data-testid="pick-list-edit-cancel"
          >
            <IconX />
          </Button>
        </li>
      )
    }

    // A condition somewhere reads this option by its category uuid. Removing it
    // leaves that gate pointing at nothing, which the service rejects on the
    // next save of the flow — so the option is marked and its delete locked.
    const isGating = isOptionGated(option, gatedOperands)

    return (
      <li key={option.clientId} className="pick-list-options__row">
        <div className="pick-list-options__text">
          <span className="pick-list-options__name">{option.name}</span>
          {!isEmpty(option.description) && (
            <span className="pick-list-options__description">{option.description}</span>
          )}
        </div>
        {isGating && (
          <Pill
            xs
            outlined
            brand
            tooltip="A field's condition reads this option"
            dataTestId={`pick-list-gating-${option.clientId}`}
          >
            In use
          </Pill>
        )}
        {!inheritedFromDefault && (
          <>
            <Button
              basic
              small
              type="button"
              tooltip="Edit option"
              onClick={() => handleStartEdit(option)}
              data-testid={`pick-list-edit-${option.clientId}`}
            >
              <IconPencil />
            </Button>
            <Button
              basic
              small
              square
              type="button"
              disabled={isGating}
              disabledTooltip="Option is being used to gate another field, remove that gate to remove the option"
              tooltip="Remove option"
              onClick={() => handleRemoveOption(option.clientId)}
              data-testid={`pick-list-delete-${option.clientId}`}
            >
              <IconTrash />
            </Button>
          </>
        )}
      </li>
    )
  }

  const renderBody = () => {
    if (loading) {
      return (
        <div className="pick-list-options__loader">
          <Loader data-testid="pick-list-loader" />
        </div>
      )
    }
    if (errored) {
      return <ErrorMessage content="Failed to load options." />
    }
    return (
      <>
        {onLinkToDefault && (
          <Banner
            dataTestId="pick-list-own-copy-banner"
            informative
            header="These options belong to this configuration only"
          >
            <p>
              This configuration keeps its own copy of these options, so edits here stay with it.
              Linking back uses your organization default options again and leaves this copy behind.
            </p>
          </Banner>
        )}
        {appliesToInheritingPlaybooks && (
          <Banner
            dataTestId="pick-list-shared-banner"
            caution
            header="These options apply to every playbook that inherits this field"
          >
            <p>
              Saving changes here updates the options everywhere the field is inherited. A playbook
              that has broken its options off the default keeps its own copy.
            </p>
          </Banner>
        )}
        {inheritedFromDefault && (
          <Banner
            dataTestId="pick-list-inherited-banner"
            informative
            header="These options are shared with every playbook that inherits this field"
          >
            <p>
              These options come from your organization defaults, so every playbook that inherits
              this field resolves against them. Break from the default to give this configuration
              its own copy.
            </p>
          </Banner>
        )}
        <div className="pick-list-options__search">
          <div className="search-input full-width">
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={inheritedFromDefault ? 'Search options' : 'Search or add an option'}
              data-testid="pick-list-search"
            />
            <IconSearch />
          </div>
          <span className="pick-list-options__count" data-testid="pick-list-count">
            {options.length} / {MAX_OPTIONS}
          </span>
        </div>
        <div className="pick-list-options__results">
          {isEmpty(options) && !showCreate ? (
            <p className="muted-text">This field has no options yet.</p>
          ) : (
            <ul className="pick-list-options__list" data-testid="pick-list-options">
              {showCreate && (
                <li className="pick-list-options__row" key="__create__">
                  <Button
                    text
                    type="button"
                    className="pick-list-options__create"
                    disabled={queryTooLong || atLimit}
                    disabledTooltip={atLimit ? AT_LIMIT_MESSAGE : TOO_LONG_MESSAGE}
                    onClick={handleCreateOption}
                    data-testid="pick-list-create-option"
                  >
                    <IconPlus />
                    Create &quot;{query}&quot;
                  </Button>
                </li>
              )}
              {visibleOptions.map(renderOptionRow)}
            </ul>
          )}
        </div>
      </>
    )
  }

  return (
    <BasicModal
      show={show}
      onClose={onClose}
      title={`"${fieldName}" options`}
      size="tiny"
      scrolling
    >
      <div className="pick-list-options">{renderBody()}</div>
      <div className="pick-list-options__footer">
        {inheritedFromDefault ? (
          onBreakFromDefault && (
            <Button
              primary
              type="button"
              onClick={() => onBreakFromDefault(options)}
              disabled={loading || errored}
              tooltip="Copy these options into this configuration so you can edit them here"
              data-testid="pick-list-break-from-default"
            >
              Break From Default
            </Button>
          )
        ) : (
          <>
            {onLinkToDefault && (
              <Button
                type="button"
                onClick={onLinkToDefault}
                disabled={loading || errored || saving}
                tooltip="Use your organization default options again - this configuration's own copy, and any options only it has, are left behind"
                data-testid="pick-list-link-to-default"
              >
                Use Organization Default
              </Button>
            )}
            {onApplyForTesting && (
              <Button
                type="button"
                onClick={handleApplyForTesting}
                disabled={optionsInvalid}
                tooltip="Use these options for the next test run without saving them"
                data-testid="pick-list-apply-test"
              >
                Apply for testing
              </Button>
            )}
            <Button
              primary
              type="button"
              onClick={handleSave}
              loading={saving}
              disabled={loading || errored || saving}
              data-testid="pick-list-save"
            >
              Save changes
            </Button>
          </>
        )}
      </div>
    </BasicModal>
  )
}
